import { cn } from '@web-app/lib/utils';
import type { AvailabilityReminder } from '@web-app/server/services/availability/availability-reminder.utils';
import {
  formatHoursAsDuration,
  formatShortDateRange,
} from '@web-app/utils/format.utils';
import { LuCalendarDays, LuPlus, LuTriangleAlert } from 'react-icons/lu';

export function AvailabilityReminderDetails({
  reminder,
}: {
  reminder: AvailabilityReminder;
}) {
  const affectedWeeks = reminder.weeks.filter(
    (week) => week.shortfallHours > 0,
  );
  const blockedHours = reminder.weeks.reduce(
    (total, week) => total + week.externalCalendarBlockedHours,
    0,
  );

  return (
    <div className="@container space-y-4">
      {affectedWeeks.length > 0 && (
        <div
          role="table"
          aria-label="Weekly availability shortfalls"
          className="text-sm"
        >
          <div role="rowgroup" className="hidden @lg:block">
            <div
              role="row"
              className="grid grid-cols-2 gap-4 px-4 pb-2 text-xs font-medium text-muted-foreground"
            >
              <span role="columnheader">Week</span>
              <span role="columnheader">To add</span>
            </div>
          </div>
          <div
            role="rowgroup"
            className="space-y-3 @lg:space-y-0 @lg:overflow-hidden @lg:rounded-lg @lg:border @lg:border-border"
          >
            {affectedWeeks.map((week) => {
              const isEmpty = week.totalHours === 0;
              return (
                <div
                  key={week.startDate}
                  role="row"
                  className="grid grid-cols-2 items-start gap-4 rounded-lg border border-border p-4 @lg:rounded-none @lg:border-0 @lg:border-b @lg:last:border-b-0"
                >
                  <div role="rowheader" className="min-w-0">
                    <p className="font-semibold">
                      {formatShortDateRange(week.startDate, week.endDate)}
                    </p>
                    {week.startDate === reminder.weeks[0].startDate && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        This week
                      </p>
                    )}
                    {isEmpty && (
                      <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-700">
                        <LuTriangleAlert
                          className="size-3.5 shrink-0"
                          aria-hidden
                        />
                        No time scheduled
                      </p>
                    )}
                  </div>
                  <div role="cell" className="min-w-0">
                    <p className="mb-1 text-xs text-muted-foreground @lg:hidden">
                      To add
                    </p>
                    <span
                      className={cn(
                        'inline-flex items-start gap-1 rounded-md px-2 py-1 text-sm font-semibold',
                        isEmpty
                          ? 'bg-red-50 text-red-800'
                          : 'bg-orange-50 text-orange-800',
                      )}
                    >
                      <LuPlus
                        className="mt-0.5 size-3.5 shrink-0"
                        aria-hidden
                      />
                      {formatHoursAsDuration(week.shortfallHours)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {blockedHours > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-3 text-sm text-orange-900">
          <LuCalendarDays className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div>
            <p className="font-semibold">
              Your synced calendar is blocking time
            </p>
            <p className="mt-1 leading-relaxed">
              {formatHoursAsDuration(blockedHours)} of open time is blocked by
              outside events. Review these events in your calendar.
            </p>
          </div>
        </div>
      )}
      <div className="space-y-1 text-xs leading-relaxed text-muted-foreground">
        <p>
          This week + the next 3 weeks · Monday–Sunday · {reminder.timezone}
        </p>
        <p>This reminder clears when the availability gaps are filled.</p>
      </div>
    </div>
  );
}
